/*
  Wishlist state (snippets/wishlist-state.liquid).

  One Alpine store, `xWishlist`, so every heart on the page — Liquid cards,
  Nosto-rendered cards, carousels, the PDP, quick view — and the header count
  read the same list. Nosto writes its cards into the light DOM, so Alpine
  initialises a heart inside one like any other markup; nothing here needs a
  re-init hook.

  The list loads at store level. Deliberately NOT from x-init: x-data plus
  x-init double-invokes in this theme.

  Using it from markup:

    <button type="button"
      x-data
      @click.prevent.stop="$store.xWishlist.toggle({{ product.id }})"
      :aria-pressed="$store.xWishlist.has({{ product.id }})"
      :disabled="!$store.xWishlist.ready || $store.xWishlist.isPending({{ product.id }})">

  `.prevent.stop` matters on cards: the whole card is a link.

  Changes are optimistic — the heart flips at once and flips back if Engine
  refuses. Nothing here shows a message; it dispatches events on `document`
  for the save UI to react to:

    wishlist:changed         { productId, saved, count }
    wishlist:error           { productId, message }
    wishlist:login-required  { productId, anchor }  — a signed-out (or
                             expired) toggle; `anchor` is the heart pressed,
                             when the caller passed it to toggle()

  SAVED AFTER SIGN-IN. The login prompt calls rememberPending() before sending
  the customer to log in or register. The next page they reach signed in — the
  one they came from, or wherever registration lands them — saves it.

  THE BROWSER'S COPY. The list is kept in localStorage, under the customer's
  id, so most page views ask Engine nothing and every heart is right at first
  paint:

  - A copy fetched from Engine less than FRESH_FOR_MS ago is used as it is.
  - An older copy is shown at once and checked with Engine in the background —
    on page load, and when the tab comes back into view.
  - Every change Engine confirms is written into the copy, so the next page
    needs no request. The age is NOT reset by a change: it counts from the last
    full fetch, which is what brings in changes made on another device.
  - A change that got no clear answer (network failure, a 5xx) ages the copy
    out, so the next page or tab focus fetches rather than trusting a guess.
    Done by zeroing the fetch time rather than with a flag, so a later change
    writing the copy carries it forward instead of clearing it.
  - Other tabs follow along through the `storage` event.
  - A page with nobody signed in deletes every copy, so a shared computer does
    not show one customer's hearts to the next.

  A stale copy cannot cause a wrong write: Engine answers saving something
  already saved with a 200 and removing something already gone with a 404,
  and both are treated as success.

  Data comes from Engine over the Shopify App Proxy (Modules/Wishlist). It
  holds product ids only: prices, stock and personalisation are Shopify's and
  the theme's, rendered live per market.
*/
(() => {
  const config = window.WishlistConfig || { root: '/apps/engine/wishlist', signedIn: false, customerId: null };

  const FRESH_FOR_MS = 60 * 1000;
  const CACHE_PREFIX = 'wishlist:v1:';
  const PENDING_KEY = 'wishlist:pending';
  // Long enough to register and confirm an email; short enough that a
  // product chosen last week is not saved out of nowhere.
  const PENDING_FOR_MS = 30 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = config.requestTimeoutMs || 15 * 1000;
  const cacheKey = config.customerId ? `${CACHE_PREFIX}${config.customerId}` : null;

  class RequestError extends Error {
    constructor(message, status, body = {}) {
      super(message);
      this.status = status;
      this.body = body;
    }
  }

  async function request(path, options = {}) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    try {
      return await send(path, options, controller);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async function send(path, options, controller) {
    let response;

    try {
      response = await fetch(`${config.root}${path}`, {
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
        headers: {
          Accept: 'application/json',
          // Engine refuses a POST that is not JSON — its guard against a
          // hostile site submitting a form in the customer's browser.
          'Content-Type': 'application/json',
          // ngrok's free-plan interstitial triggers on the browser User-Agent,
          // which the App Proxy forwards. Inert on the real domain.
          'ngrok-skip-browser-warning': '1',
          ...(options.headers || {}),
        },
      });
    } catch (error) {
      // No answer at all: status 0, which callers treat as "unknown outcome".
      throw new RequestError('We could not reach your wishlist. Please check your connection.', 0);
    }

    let body;
    try {
      body = await response.json();
    } catch (error) {
      if (controller && controller.signal.aborted) {
        throw new RequestError('We could not reach your wishlist. Please check your connection.', 0);
      }
      body = {};
    }

    if (!response.ok) {
      // Laravel validation errors arrive as { errors: { field: [msg] } }.
      const message = (body.errors && Object.values(body.errors)[0]?.[0]) || body.message || 'Something went wrong. Please try again.';
      throw new RequestError(message, response.status, body);
    }

    return body;
  }

  // Whether a failed write may still have been applied by Engine.
  const outcomeUnknown = (error) => error.status === 0 || error.status >= 500;

  const cache = {
    read() {
      if (!cacheKey) return null;
      try {
        const value = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        return value && Array.isArray(value.items) ? value : null;
      } catch (error) {
        return null;
      }
    },

    write(value) {
      if (!cacheKey) return;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(value));
      } catch (error) {
        // Private mode or a full quota: the page still works, it just asks
        // Engine every time.
      }
    },

    clearAll() {
      try {
        for (let i = localStorage.length - 1; i >= 0; i -= 1) {
          const key = localStorage.key(i);
          if (key && key.startsWith(CACHE_PREFIX)) localStorage.removeItem(key);
        }
      } catch (error) {
        /* nothing to clear */
      }
    },

    isFresh(value) {
      return Boolean(value) && Date.now() - value.fetchedAt < FRESH_FOR_MS;
    },
  };

  const marketQuery = () => {
    const params = new URLSearchParams();
    if (config.country) params.set('country', config.country);
    if (config.market) params.set('market', config.market);
    const query = params.toString();
    return query ? `?${query}` : '';
  };

  const idOf = (productId) => String(productId);

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const fetchList = () => request('/list').then((list) => ({ list, fetchedAt: Date.now() }), (error) => ({ error }));

  if (!config.signedIn) cache.clearAll();

  // Decided now rather than in the store's init(): Alpine boots last
  // (vendors.js), and a request that waited for it would leave every heart
  // indeterminate for the whole JS boot. Settled rather than left rejected,
  // since nothing awaits it until Alpine starts.
  const cached = config.signedIn ? cache.read() : null;
  const boot = !config.signedIn
    ? Promise.resolve({ signedOut: true })
    : cached
      ? Promise.resolve({ list: cached, fetchedAt: cached.fetchedAt, fromCache: true })
      : fetchList();

  // Set by the store's init(): the moment the first list has been applied.
  let loaded = boot;

  document.addEventListener('alpine:init', () => {
    Alpine.store('xWishlist', {
      signedIn: config.signedIn,

      // 'loading' | 'ready' | 'error' | 'signed-out'. Hearts should render as
      // indeterminate until ready, so they never flash the wrong state.
      status: config.signedIn ? 'loading' : 'signed-out',

      // Newest first, as Engine lists them. An item saved on this page has no
      // handle until the next fetch; the wishlist page reads handles, and
      // calls refresh() for its own list.
      items: [],

      // Lookups by id, kept beside `items` so has() is not a scan per heart.
      saved: {},
      pending: {},

      count: 0,

      // When the list in hand last came from Engine, and a counter bumped by
      // every change — a background fetch that started before a change must
      // not overwrite it when it lands.
      fetchedAt: 0,
      generation: 0,
      writes: 0,
      overlapped: false,

      get ready() {
        return this.status === 'ready';
      },

      // Alpine calls this once, when the store is registered. Clicks that
      // arrive while it is still loading await `loaded` rather than applying
      // the list themselves, so a second click cannot wipe out the first one's
      // optimistic change by re-applying the list underneath it.
      async init() {
        loaded = boot.then((result) => {
          this.apply(result);
          if (result.fromCache && !cache.isFresh(result.list)) this.revalidate();
        });
        await loaded;

        this.savePending();

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') this.revalidate();
        });
        window.addEventListener('online', () => this.revalidate());

        window.addEventListener('storage', (event) => {
          if (event.key !== cacheKey || !event.newValue) return;
          try {
            this.adopt(JSON.parse(event.newValue));
          } catch (error) {
            /* a half-written value; the next change or fetch corrects it */
          }
        });
      },

      has(productId) {
        return this.saved[idOf(productId)] === true;
      },

      isPending(productId) {
        return this.pending[idOf(productId)] === true;
      },

      async toggle(productId, anchor = null) {
        return this.has(productId) ? this.remove(productId, anchor) : this.add(productId, anchor);
      },

      async add(productId, anchor = null) {
        const id = idOf(productId);

        if (!(await this.ensureReady(id, anchor)) || this.pending[id] || this.saved[id]) return;

        this.generation += 1;
        this.pending[id] = true;
        this.saved[id] = true;
        this.items.unshift({ product_id: id, handle: null, saved_at: new Date().toISOString() });
        this.count += 1;
        this.beginWrite();

        try {
          const body = await request(marketQuery(), {
            method: 'POST',
            body: JSON.stringify({ product_id: id }),
          });

          if (!this.overlapped) this.count = body.count;
          this.persist();
          emit('wishlist:changed', { productId: id, saved: true, count: this.count });
        } catch (error) {
          delete this.saved[id];
          this.items = this.items.filter((item) => item.product_id !== id);
          this.count = Math.max(0, this.count - 1);
          this.fail(id, error, anchor);
        } finally {
          delete this.pending[id];
          this.endWrite();
        }
      },

      async remove(productId, anchor = null) {
        const id = idOf(productId);

        if (!(await this.ensureReady(id, anchor)) || this.pending[id] || !this.saved[id]) return;

        const index = this.items.findIndex((item) => item.product_id === id);
        const removed = index === -1 ? null : this.items[index];

        this.generation += 1;
        this.pending[id] = true;
        delete this.saved[id];
        if (removed) this.items.splice(index, 1);
        this.count = Math.max(0, this.count - 1);
        this.beginWrite();

        try {
          const body = await request(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
          if (!this.overlapped) this.count = body.count;
          this.persist();
          emit('wishlist:changed', { productId: id, saved: false, count: this.count });
        } catch (error) {
          // Already gone — another tab or device removed it. The state wanted
          // is the state held.
          if (error.status === 404) {
            if (typeof error.body.count === 'number' && !this.overlapped) this.count = error.body.count;
            this.persist();
            emit('wishlist:changed', { productId: id, saved: false, count: this.count });
            return;
          }

          this.saved[id] = true;
          if (removed) this.items.splice(Math.min(index, this.items.length), 0, removed);
          this.count += 1;
          this.fail(id, error, anchor);
        } finally {
          delete this.pending[id];
          this.endWrite();
        }
      },

      beginWrite() {
        if (this.writes > 0) this.overlapped = true;
        this.writes += 1;
      },

      endWrite() {
        this.writes = Math.max(0, this.writes - 1);
        if (this.writes > 0 || !this.overlapped) return;
        this.overlapped = false;
        this.refresh();
      },

      // Remember a product a signed-out customer tried to save, for
      // savePending() to save once they are back signed in.
      rememberPending(productId) {
        try {
          sessionStorage.setItem(PENDING_KEY, JSON.stringify({ productId: idOf(productId), at: Date.now() }));
        } catch (error) {
          /* storage blocked: they simply save it again after signing in */
        }
      },

      // Taken out of storage before saving, so a failed save is not retried on
      // every page for ever.
      savePending() {
        if (this.status !== 'ready') return;

        let pending = null;
        try {
          pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
          sessionStorage.removeItem(PENDING_KEY);
        } catch (error) {
          return;
        }

        if (!pending || !pending.productId || Date.now() - pending.at > PENDING_FOR_MS) return;

        this.add(pending.productId);
      },

      // Fetch from Engine now, whatever the age of the copy — e.g. the wishlist
      // page, which needs handles for items saved since the copy was made.
      async refresh() {
        if (!this.signedIn) return;

        const generation = this.generation;
        const result = await fetchList();

        // A change landed while this was in flight; its own answer is newer
        // than this list. The next check picks up anything else.
        if (generation !== this.generation || this.hasPending()) return false;

        this.apply(result);
        return true;
      },

      // Fetch only when the copy is older than FRESH_FOR_MS, or when there is
      // no list at all because the last attempt failed.
      revalidate() {
        if (this.status === 'error') return this.refresh();
        if (this.status !== 'ready' || Date.now() - this.fetchedAt < FRESH_FOR_MS) return;
        this.refresh();
      },

      apply(result) {
        if (result.signedOut) {
          this.status = 'signed-out';
          return;
        }

        if (result.error) {
          if (result.error.status === 401) {
            this.signedIn = false;
            this.status = 'signed-out';
            cache.clearAll();
            return;
          }

          // Keep showing a copy we already have rather than blanking every
          // heart over one failed check.
          if (this.status !== 'ready') this.status = 'error';
          return;
        }

        this.setList(result.list.items, result.list.count, result.fetchedAt);
        this.status = 'ready';

        if (!result.fromCache) this.persist();
      },

      // Another tab changed the list: take its copy, unless this tab is mid-change.
      adopt(value) {
        if (!value || !Array.isArray(value.items) || this.hasPending()) return;

        this.generation += 1;
        this.setList(value.items, value.count, value.fetchedAt);
        if (this.status !== 'signed-out') this.status = 'ready';
      },

      setList(items, count, fetchedAt) {
        const list = Array.isArray(items) ? items : [];

        this.items = list;
        this.saved = Object.fromEntries(list.map((item) => [idOf(item.product_id), true]));
        this.count = typeof count === 'number' ? count : list.length;
        this.fetchedAt = fetchedAt || 0;
      },

      persist() {
        cache.write({ items: this.items, count: this.count, fetchedAt: this.fetchedAt });
      },

      hasPending() {
        return Object.keys(this.pending).length > 0;
      },

      // A click before the list has loaded waits for it rather than guessing
      // whether the product is saved. Signed out, the save UI takes over.
      async ensureReady(id, anchor = null) {
        if (this.status === 'loading') await loaded;

        // The list failed to load earlier (a network blip, Engine down). Try
        // once more rather than refusing silently.
        if (this.status === 'error') await this.refresh();

        if (this.status === 'error') {
          emit('wishlist:error', { productId: id, message: 'We could not load your wishlist. Please try again.' });
          return false;
        }

        if (this.status === 'signed-out') {
          emit('wishlist:login-required', { productId: id, anchor });
          return false;
        }

        return this.status === 'ready';
      },

      fail(id, error, anchor = null) {
        // The session ended since the page loaded.
        if (error.status === 401) {
          this.signedIn = false;
          this.status = 'signed-out';
          cache.clearAll();
          emit('wishlist:login-required', { productId: id, anchor });
          return;
        }

        // Engine may have applied it. The heart has been put back, which may
        // be wrong, so the copy is aged out for the next page to fetch.
        if (outcomeUnknown(error)) {
          this.fetchedAt = 0;
          this.persist();
        }

        emit('wishlist:error', { productId: id, message: error.message });
      },
    });

    // The login prompt (snippets/wishlist-login-prompt.liquid): a popover by
    // the heart on desktop, a bottom sheet on mobile.
    Alpine.data('xWishlistLogin', () => ({
      open: false,
      mobile: false,
      productId: null,
      anchor: null,
      top: 0,
      left: 0,
      width: 336,
      arrowLeft: 0,

      init() {
        document.addEventListener('wishlist:login-required', (event) => this.show(event.detail || {}));
      },

      get loginHref() {
        return this.withReturn(this.$root.dataset.loginUrl);
      },

      get registerHref() {
        return this.withReturn(this.$root.dataset.registerUrl);
      },

      // Back to this page after signing in. Built here because the card markup
      // may have come from a section request for a different path. Both
      // parameters: new customer accounts read return_to, classic ones
      // return_url, and each ignores the other.
      withReturn(base) {
        const returnUrl = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
        return `${base}${base.includes('?') ? '&' : '?'}return_to=${returnUrl}&return_url=${returnUrl}`;
      },

      show({ productId = null, anchor = null }) {
        this.productId = productId;
        this.anchor = anchor && anchor.isConnected ? anchor : null;
        this.mobile = !this.anchor || !window.matchMedia('(min-width: 768px)').matches;

        if (!this.mobile) this.place();

        this.open = true;
      },

      // Under the heart, right edges aligned, kept inside the viewport. In page
      // coordinates, so it scrolls with the card rather than floating over it.
      // The width is set here and bound inline, not by a class: it has to be
      // known before the panel shows, and must not depend on a CSS build.
      place() {
        const WIDTH = Math.min(336, document.documentElement.clientWidth - 32);
        this.width = WIDTH;
        const GUTTER = 16;
        const rect = this.anchor.getBoundingClientRect();
        const minLeft = window.scrollX + GUTTER;
        const maxLeft = window.scrollX + document.documentElement.clientWidth - WIDTH - GUTTER;

        this.top = Math.round(rect.bottom + window.scrollY + 16);
        this.left = Math.round(Math.min(Math.max(rect.right + window.scrollX - WIDTH, minLeft), maxLeft));

        const CARET = 12;
        const heartCentre = rect.left + window.scrollX + rect.width / 2;
        const ideal = heartCentre - this.left - 1 - CARET / 2;
        this.arrowLeft = Math.round(Math.min(Math.max(ideal, 16), WIDTH - 2 - 16 - CARET));
      },

      close() {
        const panel = document.getElementById('WishlistLoginPanel');
        const hadFocus = panel && panel.contains(document.activeElement);

        this.open = false;

        // Back to the heart for keyboard users, as a dialog should.
        if (hadFocus && this.anchor) this.anchor.focus({ preventScroll: true });
      },

      // A click on the heart that opened it is not "outside": without this the
      // same press that opens the popover would close it again.
      outside(event) {
        if (!this.open) return;
        if (this.anchor && this.anchor.contains(event.target)) return;
        this.close();
      },

      remember() {
        if (this.productId) Alpine.store('xWishlist').rememberPending(this.productId);
      },
    }));
  });
})();
